import React, { Suspense, lazy, useCallback, useState } from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { useIdentity } from './hooks/useIdentity';
import useComposerPrefill from './hooks/useComposerPrefill';
import { OnboardingScreen } from './features/onboarding';
import { Chrome } from './features/nav';
import { ChatRegion } from './features/chat';
import { BubblesBg } from './components';
import { ShareSheetHost } from './features/beam/ShareSheetHost';
import { FirebaseHealthListener } from './components/FirebaseHealthListener';
import {
  GLOB,
  PARTY_CODE_PATTERN,
  pathForPartyCode,
} from './router/paths';

// Lazy: MembersPanel only mounts on first open; PartyNotFound only on
// malformed deep links. Neither belongs in the initial bundle.
const MembersPanel = lazy(() => import('./features/members/MembersPanel'));
const PartyNotFound = lazy(() => import('./features/chat/PartyNotFound'));

/**
 * ChatShell — the authenticated layout. Wraps Chrome + ChatRegion + the
 * global MembersPanel drawer. Rendered when a handle is present and the URL
 * route resolves to a valid room.
 */
function ChatShell() {
  const [membersOpen, setMembersOpen] = useState(false);
  const openMembers = useCallback(() => setMembersOpen(true), []);
  const closeMembers = useCallback(() => setMembersOpen(false), []);
  const { prefill } = useComposerPrefill();
  const { setPartyCode } = useIdentity();

  // Quick-reply icon in the members panel: close the panel + seed the
  // composer with `@handle ` and focus it. The Composer reads the pending
  // slot via useComposerPrefill in its own effect.
  const handleReplyTo = useCallback(
    (handle) => {
      prefill(handle);
      setMembersOpen(false);
    },
    [prefill],
  );

  // Sidebar Rooms list: clicking a row navigates via the provider's
  // setPartyCode (URL + storage + cookie all the way through). React
  // Router collapses same-URL navigations so clicking the active row
  // is a safe no-op.
  const handleSelectRoom = useCallback(
    (code) => setPartyCode(code),
    [setPartyCode],
  );

  return (
    <>
      <BubblesBg density="mobile" seed="glacier-mobile" />
      <BubblesBg density="desktop" seed="glacier-desktop" />
      <Chrome
        onMembersClick={openMembers}
        onSelectRoom={handleSelectRoom}
      >
        <ChatRegion />
      </Chrome>
      <Suspense fallback={null}>
        {membersOpen && (
          <MembersPanel
            isOpen
            onClose={closeMembers}
            variant="drawer"
            onReplyTo={handleReplyTo}
          />
        )}
      </Suspense>
    </>
  );
}

/**
 * PartyRoute — validates the `:code` URL param. Valid 4-letter codes render
 * ChatShell (IdentityProvider's probe handles "exists but empty" → expired
 * banner + redirect). Malformed codes render the F6.3 PartyNotFound state.
 */
function PartyRoute() {
  const { code } = useParams();
  const upper = (code || '').toUpperCase();
  if (!PARTY_CODE_PATTERN.test(upper)) {
    return (
      <Suspense fallback={null}>
        <PartyNotFound code={code} />
      </Suspense>
    );
  }
  // Canonicalize: if the URL had a lowercase code, redirect to upper-case so
  // the URL always reads cleanly in the address bar.
  if (code !== upper) {
    return <Navigate to={pathForPartyCode(upper)} replace />;
  }
  return <ChatShell />;
}

/**
 * IndexRedirect — handles `/`. If we have a remembered last partyCode in
 * localStorage (which mirrors any party the user previously visited), redirect
 * there. Otherwise land on /glob.
 */
function IndexRedirect() {
  const last = (() => {
    try {
      return localStorage.getItem('partyCode') || GLOB;
    } catch {
      return GLOB;
    }
  })();
  return <Navigate to={pathForPartyCode(last)} replace />;
}

function App() {
  const { hasHandle } = useIdentity();

  // Onboarding overlays whichever route the user landed on so a deep link
  // like /p/XKQP still works for a new visitor — they enter a handle, the
  // route persists, and they land directly in the right party.
  if (!hasHandle) {
    return (
      <>
        <OnboardingScreen />
        <FirebaseHealthListener />
      </>
    );
  }

  // ShareSheetHost is a singleton overlay that listens to the mesh's pending
  // file-offer queue and renders the F14.2 receiver-consent sheet whenever an
  // offer is waiting. Mounted at the root so it's available from every route.
  //
  // FirebaseHealthListener surfaces Firestore quota / permission / unavailable
  // errors as toasts (deduped + rate-limited by lib/firebase-health.js).
  return (
    <>
      <Routes>
        <Route path="/" element={<IndexRedirect />} />
        <Route path="/glob" element={<ChatShell />} />
        <Route path="/p/:code" element={<PartyRoute />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <ShareSheetHost />
      <FirebaseHealthListener />
    </>
  );
}

export default App;
